import { Capacitor } from '@capacitor/core';
import deviceService from './deviceService';
import mobileDevicesApi from '../api/mobileDevicesApi';

/**
 * Device Registration Service
 *
 * Povezuje stabilni deviceId instalacije sa push tokenom i registruje uređaj
 * na backend-u. Isti deviceId se koristi i kada se push token promeni - backend
 * tada update-uje postojeći EmployeeDevice (ne pravi novi zapis).
 *
 * Servis je idempotentan u okviru jednog pokretanja aplikacije: isti token se
 * ne šalje više puta. Novi token (npr. FCM/APNs rotacija) se šalje ponovo sa
 * istim deviceId-om.
 */

let lastRegisteredPushToken = null;
let inFlightRegistration = null;

const isNativePlatform = () => Capacitor.isNativePlatform();

const deviceRegistrationService = {
  /**
   * Registruj trenutni uređaj za push notifikacije.
   * Na web-u je no-op i ne poziva backend.
   *
   * @param {string} pushToken - APNs/FCM token dobijen od Capacitor-a
   * @returns {Promise<Object|null>} Odgovor backend-a ili null
   */
  async registerCurrentDevice(pushToken) {
    if (!isNativePlatform() || !pushToken) return null;
    if (pushToken === lastRegisteredPushToken) return null;

    if (inFlightRegistration && inFlightRegistration.pushToken === pushToken) {
      return inFlightRegistration.promise;
    }

    const promise = (async () => {
      const [deviceId, platform, deviceName] = await Promise.all([
        deviceService.getDeviceId(),
        Promise.resolve(deviceService.getPlatform()),
        deviceService.getDeviceName(),
      ]);

      if (!deviceId || !platform) return null;

      const result = await mobileDevicesApi.registerDevice({
        deviceId,
        platform,
        deviceName,
        pushToken,
      });

      // Tek posle uspešne registracije pamtimo token - neuspeh može da se ponovi.
      lastRegisteredPushToken = pushToken;

      if (process.env.NODE_ENV !== 'production') {
        console.info('Mobilni uređaj je registrovan za push notifikacije.', {
          platform,
          deviceId,
        });
      }

      return result;
    })().finally(() => {
      inFlightRegistration = null;
    });

    inFlightRegistration = { pushToken, promise };
    return promise;
  },

  /**
   * Da li je dati push token već uspešno registrovan u ovoj sesiji.
   * @param {string} pushToken
   * @returns {boolean}
   */
  isTokenRegistered(pushToken) {
    return Boolean(pushToken) && pushToken === lastRegisteredPushToken;
  },

  /**
   * Resetuje samo in-memory deduplikaciju (npr. na logout). Ne briše deviceId
   * niti backend zapis - sledeća prijava ponovo registruje isti uređaj, što je
   * bitno kada se na istom uređaju prijavi drugi zaposleni.
   */
  resetSession() {
    lastRegisteredPushToken = null;
    inFlightRegistration = null;
  },
};

export default deviceRegistrationService;
